import { useEffect, useState } from "react";

import {
  Loader,
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";

import { useApiRequest, useDownloadPathwayPDF } from "@lib/hooks";
import container from "@src/container";
import { TraineeCareerPathwaySection } from "@views/shared";

import { TraineeCareerPathwayToolbar } from "./trainee.career-pathway.toolbar";
import { TraineeTrekCareers } from "./trainee.trek.careers";

import "./trainee.classroom.trek.tab.scss";

const calculateExploredVr = (occupationCodes, occupationsMap, simsExplored) => {
  // Return undefined if no ocupation code or occupation code not in list of all sims' occupation codes
  if (
    occupationCodes == undefined ||
    occupationCodes.length === 0 ||
    !Object.keys(occupationsMap).some((occ) => occupationCodes.includes(occ))
  ) {
    return;
  }

  // Return true if user has explored sim with the same occupation code as the trek career
  const simsExploredSet = new Set(simsExplored);
  for (let occ of occupationCodes) {
    if (occupationsMap[occ].some((leid) => simsExploredSet.has(leid))) {
      return true;
    }
  }
  return false;
};

export function TraineeClassroomTrekTab({ trainee, classroom }) {
  const [data, setData] = useState([]);
  const [notification, setNotification] = useState();
  const { isGeneratingPDF, doDownloadPathwayPDF } = useDownloadPathwayPDF({
    classroom,
    trainee,
    setNotification,
  });
  const { trekService, simulationsService, insightsService } = container;
  const { userId } = trainee ?? {};

  const { loading: loadingCareers, response: careersResponse } = useApiRequest(
    () =>
      trekService
        .getUsersExportData({
          classroomId: classroom.classroomId,
          userId,
          options: {
            include_career_goals: false,
            include_lightcast_data: true,
          },
        })
        .then((response) => {
          const {
            careerPlans,
            exploredCareers,
            favoritedCareers,
            selectedCareer,
            budgetPlans,
          } = response[userId] ?? {};
          const favorites = new Set(favoritedCareers?.map((f) => f.transfrId));
          const careerPlansMap = new Map(
            careerPlans?.map((cp) => [cp.career.transfrId, cp.id])
          );
          const completedBudgetPlans = new Set(
            budgetPlans
              ?.filter((bp) => !!bp.budgetCompletedAt)
              .map((bp) => bp.career.transfrId)
          );

          return exploredCareers?.map((career) => ({
            ...career,
            careerPlanId: careerPlansMap.get(career.transfrId),
            favorite: favorites.has(career.transfrId),
            selected: selectedCareer?.transfrId === career.transfrId,
            isBudgetCreated: completedBudgetPlans.has(career.transfrId),
          }));
        })
  );

  const { response: simulations, loading: loadingSims } = useApiRequest(() =>
    simulationsService.getClassroomSimulations(classroom.classroomId)
  );

  const { response: masteryData, loading: loadingMastery } = useApiRequest(() =>
    insightsService.getUserCEMasteryInfo(userId)
  );

  const mergeTraineeTrekData = (careersData, simulations, masteryData) => {
    const leidsExplored = [...masteryData.keys()];
    const occupationToLeidsMap = {};

    for (const item of simulations) {
      const occupationCode = item.occupationCode;
      const learningExperienceId = item.learningExperienceId;
      if (!occupationToLeidsMap[occupationCode]) {
        occupationToLeidsMap[occupationCode] = [];
      }
      occupationToLeidsMap[occupationCode].push(learningExperienceId);
    }

    // Flattening the careers explored data response
    const careerFormatted = careersData.map((c) => {
      const { cluster, occupationCodes, ...career } = c;
      const { name: clusterName, description: clusterDescription } =
        cluster || {};

      const exploredVr = calculateExploredVr(
        occupationCodes,
        occupationToLeidsMap,
        leidsExplored
      );

      return {
        ...career,
        clusterName,
        clusterDescription,
        exploredVr,
      };
    });
    setData(careerFormatted);
  };

  useEffect(() => {
    careersResponse &&
      simulations &&
      masteryData &&
      mergeTraineeTrekData(careersResponse, simulations, masteryData);
  }, [careersResponse, simulations, masteryData]);

  if (loadingCareers || loadingSims || loadingMastery) {
    return <Loader overlay />;
  }

  return (
    <>
      <div className="trainee-classroom-trek-pathway-container">
        <TraineeCareerPathwaySection
          classroom={classroom}
          downloadPathwayPDFEnabled
          user={trainee}
          toolbar={
            <TraineeCareerPathwayToolbar
              masteryData={masteryData}
              classroom={classroom}
            />
          }
        />
      </div>
      <TraineeTrekCareers
        careers={data}
        trainee={trainee}
        onDownloadPathwayPDF={doDownloadPathwayPDF}
      />
      <div className="update-notification-container">
        {isGeneratingPDF && (
          <Notification
            type={NotificationType.info}
            icon={["fa-solid", "spinner"]}
            iconConfig={{ spin: true }}
          >
            <div className="update-message">Generating PDF...</div>
          </Notification>
        )}
        {notification && (
          <Notification
            type={notification.type}
            icon={notification.icon}
            closable
            onClose={() => setNotification()}
          >
            {notification.message}
          </Notification>
        )}
      </div>
    </>
  );
}
